import React, { useRef, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { CloudUploadOutlined, DescriptionOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { privateApi } from "../../api/axiosConfig";
import { useAuth } from "../../services/auth";
import { AdminRoute } from "../../services/auth";

export default function ProductXlsUpload() {
  const { isAuthenticated } = useAuth();

  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "بارگذاری محصولات";
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    setSuccessMessage("");
    setErrorMessage("");

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith(".xls")) {
      setSelectedFile(null);
      setErrorMessage("لطفاً فقط فایل با فرمت XLS انتخاب کنید.");

      // Allow selecting the same file again after an error.
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!isAuthenticated) {
      setErrorMessage("برای بارگذاری فایل ابتدا وارد حساب کاربری شوید.");
      setTimeout(() => {
        navigate("/login");
      }, 4000);
    }

    if (!selectedFile) {
      setErrorMessage("لطفاً ابتدا یک فایل XLS انتخاب کنید.");
      return;
    }

    setUploading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const formData = new FormData();

      formData.append("file", selectedFile);

      const response = await privateApi.post(
        "/api/v1/products/upload/", 
        formData,
        {
            headers:{
                "Content-Type": "multipart/form-data",
            }
        }
    );

      setSuccessMessage(
        response?.data?.message || "فایل با موفقیت بارگذاری شد.",
      );

      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        setErrorMessage("نشست شما منقضی شده است. لطفاً دوباره وارد شوید.");
      } else {
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.response?.data?.error;

        setErrorMessage(backendMessage || "بارگذاری فایل با خطا مواجه شد.");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleChooseFile = () => {
    if (!isAuthenticated) {
      setErrorMessage("برای بارگذاری فایل ابتدا وارد حساب کاربری شوید.");
      return;
    }

    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ direction: "rtl" }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "grey.200",
          bgcolor: "#fff",
        }}
      >
        <Typography variant="h6" fontWeight={800} mb={1}>
          بارگذاری محصولات
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          فایل محصولات را انتخاب کنید تا در سیستم بارگذاری شود.
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xls"
          hidden
          onChange={handleFileChange}
        />

        <Box
          sx={{
            border: "1px dashed",
            borderColor: selectedFile ? "#8257ac" : "grey.300",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            bgcolor: "grey.50",
            mb: 2,
          }}
        >
          {selectedFile ? (
            <>
              <DescriptionOutlined
                sx={{
                  fontSize: 42,
                  color: "#8257ac",
                  mb: 1,
                }}
              />

              <Typography
                variant="body1"
                fontWeight={700}
                sx={{
                  wordBreak: "break-word",
                }}
              >
                {selectedFile.name}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadOutlined
                sx={{
                  fontSize: 42,
                  color: "grey.400",
                  mb: 1,
                }}
              />

              <Typography variant="body2" color="text.secondary" mb={2}>
                هنوز فایلی انتخاب نشده است
              </Typography>

              <Button
                variant="outlined"
                onClick={handleChooseFile}
                disabled={!isAuthenticated}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              >
                انتخاب فایل
              </Button>
            </>
          )}
        </Box>

        {selectedFile && (
          <Box
            display="flex"
            gap={1}
            justifyContent="flex-start"
            sx={{ direction: "rtl" }}
          >
            <Button
              variant="outlined"
              onClick={handleChooseFile}
              disabled={uploading}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              تغییر فایل
            </Button>

            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading || !isAuthenticated}
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUploadOutlined />
                )
              }
              sx={{
                bgcolor: "#ef4056",
                color: "#fff",
                borderRadius: 2,
                fontWeight: 700,
                "&:hover": {
                  bgcolor: "#d73247",
                },
              }}
            >
              {uploading ? "در حال بارگذاری..." : "بارگذاری فایل"}
            </Button>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessMessage("")}
          sx={{ width: "100%", direction: "rtl", textAlign: "right" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{ width: "100%", direction: "rtl", textAlign: "right" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
